import { useCallback } from "react";

import { useAtomValue, useSetAtom } from "jotai";

import { useTranslation } from "@arc/i18n";
import { useTonWallets } from "@arc/sdk";

import { isWalletMigrationFinishedAtom } from "@/atoms/user";

const V1_WALLET_DATA_KEY = "wData";

interface V1WalletData {
  currentWallet: number;
  wallets: Array<{
    address: string;
    mode: "mnemonics" | "tonconnect";
    network: string;
    privateKey?: string;
    publicKey?: string;
  }>;
}

export const MigrationResults = {
  SUCCESS: "SUCCESS",
  TONCONNECT: "TONCONNECT",
  NOTHING: "NOTHING",
} as const;

export type MigrationResult = (typeof MigrationResults)[keyof typeof MigrationResults];

export const useWalletMigration = () => {
  const { t } = useTranslation("wallet");
  const isWalletMigrationFinished = useAtomValue(isWalletMigrationFinishedAtom);
  const setIsWalletMigrationFinished = useSetAtom(isWalletMigrationFinishedAtom);

  const { _forceAddWallet } = useTonWallets();

  const tryWalletMigration = useCallback(() => {
    if (isWalletMigrationFinished) {
      return MigrationResults.NOTHING;
    }

    try {
      const wData = localStorage.getItem(V1_WALLET_DATA_KEY);
      if (wData) {
        const parsedWalletData: V1WalletData = JSON.parse(wData);
        const oldWallet = parsedWalletData.wallets[0];

        if (!oldWallet) return MigrationResults.NOTHING;

        if (oldWallet.mode === "tonconnect" || !oldWallet.privateKey || !oldWallet.publicKey) {
          return MigrationResults.TONCONNECT;
        }

        _forceAddWallet({
          privateKey: oldWallet.privateKey,
          publicKey: oldWallet.publicKey,
          address: oldWallet.address,
          walletVersion: "V4",
          walletName: t("WALLET_FROM_V1"),
        });

        return MigrationResults.SUCCESS;
      } else {
        return MigrationResults.NOTHING;
      }
    } catch {
      return MigrationResults.NOTHING;
    } finally {
      setIsWalletMigrationFinished(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWalletMigrationFinished]);

  return { tryWalletMigration, isWalletMigrationFinished };
};
